import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { fmtTs } from '../lib/globalTz';


interface DnstwistTabProps {
  fixedCustomerId?: string;
  hideCustomerSelector?: boolean;
}

interface DnstwistConfig {
  enabled: boolean;
  configured: boolean;
  scriptPath: string;
  runnerPath: string;
  runnerConfigured: boolean;
  logDir: string;
  defaultFuzzers: string[];
}

interface DnstwistSummary {
  total?: number;
  byFuzzer?: Record<string, number>;
  withA?: number;
  withAAAA?: number;
  withMx?: number;
  withNs?: number;
  withWhois?: number;
}

interface DnstwistAllSummary {
  totalGenerated?: number;
  registeredOrResolved?: number;
  allByFuzzer?: Record<string, number>;
  registeredByFuzzer?: Record<string, number>;
}

interface DnstwistAiFinding {
  domain: string;
  fuzzer?: string;
  confidence: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  reasons: string[];
  recommendedAction: string;
}

interface DnstwistAiCandidate {
  domain: string;
  technique: string;
  rationale: string;
}

interface DnstwistAiAnalysis {
  overallConfidence: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  executiveSummary: string;
  keyFindings: DnstwistAiFinding[];
  missedPermutationCandidates: DnstwistAiCandidate[];
  additionalFuzzerRecommendations: string[];
  defensiveRegistrations: string[];
  nextSteps: string[];
  model?: string;
  generatedAt?: string;
}

interface DnstwistScan {
  id: number;
  customer_id: string;
  target: string;
  fuzzers: string;
  status: string;
  requested_by_email?: string;
  result_summary?: DnstwistSummary;
  all_summary?: DnstwistAllSummary;
  ai_analysis?: DnstwistAiAnalysis;
  ai_analyzed_at?: string;
  error?: string;
  started_at?: string;
  finished_at?: string;
  created_at: string;
  updated_at: string;
}

interface DnstwistResultRow {
  domain?: string;
  fuzzer?: string;
  dns_a?: string[];
  dns_aaaa?: string[];
  dns_mx?: string[];
  dns_ns?: string[];
  whois_created?: string;
  whois_registrar?: string;
  dns_valid?: boolean;
  is_registered?: boolean;
}

interface DnstwistResults {
  id: number;
  target: string;
  fuzzers: string;
  status: string;
  scope?: 'registered' | 'all';
  summary?: DnstwistSummary;
  allSummary?: DnstwistAllSummary;
  aiAnalysis?: DnstwistAiAnalysis | null;
  aiAnalyzedAt?: string | null;
  results: DnstwistResultRow[];
  registeredResults?: DnstwistResultRow[];
  error?: string;
  startedAt?: string;
  finishedAt?: string;
}

const STATUS_CLASS: Record<string, string> = {
  submitted: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
  running: 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300',
  completed: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
  failed: 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300',
};

function statusClass(value?: string): string {
  return STATUS_CLASS[String(value ?? '').toLowerCase()] ?? STATUS_CLASS.submitted;
}

function riskClass(value?: string): string {
  switch (String(value ?? '').toLowerCase()) {
    case 'critical': return 'bg-red-100 dark:bg-red-950/50 text-red-800 dark:text-red-200';
    case 'high': return 'bg-orange-100 dark:bg-orange-950/50 text-orange-800 dark:text-orange-200';
    case 'medium': return 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200';
    case 'low': return 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-200';
    default: return 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300';
  }
}

function displayList(value?: string[]): string {
  if (!Array.isArray(value) || value.length === 0) return '-';
  return value.join(', ');
}

function displaySafeLogDir(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '/var/log/...';
  if (/^\/var\/log(\/.*)?$/i.test(raw)) return '/var/log/...';
  const normalized = raw.replace(/\\/g, '/').replace(/\/[^/]*$/g, '/...');
  return normalized || '/var/log/...';
}

function topFuzzersText(value?: Record<string, number>, limit = 8): string {
  if (!value) return '-';
  return (Object.entries(value) as Array<[string, number]>)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, count]) => `${name}: ${count}`)
    .join(' · ') || '-';
}

export function DnstwistTab({ fixedCustomerId, hideCustomerSelector = false }: DnstwistTabProps = {}) {
  const { customers, activeCustomerId, activeCustomer, setActiveCustomerId } = useCustomers();
  const { canCapability } = useAuth();

  const canStartScan = canCapability('dnstwist', 'start_scan');
  const canViewResults = canCapability('dnstwist', 'view_results');
  const canDownloadResults = canCapability('dnstwist', 'download_results');
  const canRunAiAnalysis = canCapability('dnstwist', 'run_ai_analysis');

  const effectiveCustomerId = fixedCustomerId || activeCustomerId || customers[0]?.id || '';
  const [customerId, setCustomerId] = useState(effectiveCustomerId);
  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === customerId) || activeCustomer,
    [customers, customerId, activeCustomer],
  );
  const [target, setTarget] = useState('');
  const [fuzzers, setFuzzers] = useState('');
  const [config, setConfig] = useState<DnstwistConfig | null>(null);
  const [scans, setScans] = useState<DnstwistScan[]>([]);
  const [selectedResult, setSelectedResult] = useState<DnstwistResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [runnerStarting, setRunnerStarting] = useState(false);
  const [aiAnalyzingId, setAiAnalyzingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const suggested = new URLSearchParams(window.location.search).get('dnstwistTarget');
    if (suggested && !target.trim()) {
      setTarget(suggested.trim().toLowerCase());
    }
  // Read the deep-link parameter only on first render so analysts can still edit the input.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const nextCustomerId = fixedCustomerId || activeCustomerId || customers[0]?.id || '';
    if (nextCustomerId && nextCustomerId !== customerId) {
      setCustomerId(nextCustomerId);
    }
  }, [fixedCustomerId, activeCustomerId, customers, customerId]);

  async function loadConfig() {
    try {
      const data = await apiFetch<DnstwistConfig>('/dnstwist/config');
      setConfig(data);
      if (!fuzzers && data.defaultFuzzers?.length) {
        setFuzzers(data.defaultFuzzers.join(','));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function loadScans(nextCustomerId = customerId) {
    if (!nextCustomerId) {
      setScans([]);
      setError('Select a customer before loading lookalike scans.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<{ scans: DnstwistScan[] }>(
        `/dnstwist/scans?customerId=${encodeURIComponent(nextCustomerId)}`,
      );
      setScans(data.scans ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function startScan() {
    if (!canStartScan) {
      setError('Your role is not allowed to start lookalike scans.');
      return;
    }

    if (!customerId) {
      setError('Select a customer before starting a lookalike scan.');
      return;
    }

    setStarting(true);
    setError(null);
    setSelectedResult(null);

    try {
      await apiFetch('/dnstwist/scans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, target, fuzzers }),
      });

      setTarget('');
      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  async function runConfiguredSweep() {
    if (!canStartScan) {
      setError('Your role is not allowed to start the configured lookalike scanner runner.');
      return;
    }

    setRunnerStarting(true);
    setError(null);

    try {
      await apiFetch('/dnstwist/runner', { method: 'POST' });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunnerStarting(false);
    }
  }

  async function refreshScan(scan: DnstwistScan) {
    if (!canViewResults) {
      setError('Your role is not allowed to refresh lookalike scan status.');
      return;
    }

    setError(null);
    try {
      await apiFetch(`/dnstwist/scans/${scan.id}/refresh`, { method: 'POST' });
      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function loadResults(scan: DnstwistScan, scope: 'registered' | 'all' = 'registered') {
    if (!canViewResults) {
      setError('Your role is not allowed to view lookalike scan results.');
      return;
    }

    setError(null);
    setSelectedResult(null);

    try {
      const data = await apiFetch<DnstwistResults>(
        `/dnstwist/scans/${scan.id}/results?scope=${encodeURIComponent(scope)}`,
      );
      setSelectedResult(data);
      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function downloadResults(scan: DnstwistScan, format: 'csv' | 'json' = 'csv', scope: 'registered' | 'all' = 'registered') {
    if (!canDownloadResults) {
      setError('Your role is not allowed to download lookalike scan results.');
      return;
    }

    const url = `/api/dnstwist/scans/${scan.id}/download?format=${encodeURIComponent(format)}&scope=${encodeURIComponent(scope)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function runAiAnalysis(scan: DnstwistScan) {
    if (!canRunAiAnalysis) {
      setError('Your role is not allowed to run AI analysis for lookalike scan results.');
      return;
    }

    setAiAnalyzingId(scan.id);
    setError(null);

    try {
      const data = await apiFetch<{ analysis: DnstwistAiAnalysis; aiAnalyzedAt?: string }>(
        `/dnstwist/scans/${scan.id}/ai-analysis`,
        { method: 'POST' },
      );

      if (selectedResult?.id === scan.id) {
        setSelectedResult({
          ...selectedResult,
          aiAnalysis: data.analysis,
          aiAnalyzedAt: data.aiAnalyzedAt ?? new Date().toISOString(),
        });
      } else {
        await loadResults(scan, 'registered');
      }

      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiAnalyzingId(null);
    }
  }

  useEffect(() => {
    void loadConfig();
  }, []);

  useEffect(() => {
    if (customerId) void loadScans(customerId);
  }, [customerId]);

  return (
    <div className="slide-in space-y-4">
      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">Deep-Combo Lookalike Scan</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Run local typo-squat, homoglyph, subdomain-boundary and deep-combo permutations for monitored domains. The portal stores both all generated permutations and the DNS/WHOIS-resolving subset.
            </p>
          </div>

          <div className="text-xs space-y-1 max-w-xl break-all">
            <div>Feature: {config?.enabled ? 'Enabled' : 'Disabled'}</div>
            <div>Script: {config?.configured ? 'Configured' : 'Not configured'}</div>
            <div>Runner: {config?.runnerConfigured ? 'Configured' : 'Not configured'}</div>
            <div>Log directory: {displaySafeLogDir(config?.logDir)}</div>
          </div>
        </div>

        {!config?.configured && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 text-amber-800 dark:text-amber-300 p-3 text-sm">
            Lookalike scanner script is not configured. Run the domain-impersonation installer on the VM or set the scanner script path in app settings.
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 p-4 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <h3 className="text-sm font-bold text-foreground mb-4">Start New On-Demand Scan</h3>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
          {!hideCustomerSelector && (
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Customer</label>
              <select
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setActiveCustomerId(e.target.value);
                }}
                className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
              >
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="xl:col-span-2">
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Target domain</label>
            <input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={selectedCustomer?.monitoredDomains?.[0] ?? 'example.com'}
              className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
            />
          </div>

          <div className="xl:col-span-2">
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Fuzzers</label>
            <input
              value={fuzzers}
              onChange={(e) => setFuzzers(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button
            onClick={startScan}
            disabled={starting || !canStartScan || !config?.configured || !target.trim()}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            {starting ? 'Submitting...' : 'Start Lookalike Scan'}
          </button>

          <button
            onClick={runConfiguredSweep}
            disabled={runnerStarting || !canStartScan || !config?.runnerConfigured}
            className="px-4 py-2 rounded-lg border text-sm font-semibold disabled:opacity-50"
          >
            {runnerStarting ? 'Submitting Runner...' : 'Run Configured Sweep'}
          </button>

          <button
            onClick={() => loadScans(customerId)}
            disabled={loading}
            className="px-4 py-2 rounded-lg border text-sm font-semibold disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          On-demand scans now store all generated permutations plus the registered/DNS-resolving subset. The configured sweep still writes JSONL finding files for AMA under the configured log directory.
        </p>
      </div>

      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-bold text-foreground">Recent Lookalike Scans</h3>
          {loading && <span className="text-xs text-muted-foreground">Loading...</span>}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4">Target</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Results</th>
                <th className="py-2 pr-4">Requested By</th>
                <th className="py-2 pr-4">Updated</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan) => (
                <tr key={scan.id} className="border-b last:border-b-0 align-top">
                  <td className="py-3 pr-4 font-medium text-foreground">{scan.target}</td>
                  <td className="py-3 pr-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusClass(scan.status)}`}>
                      {scan.status}
                    </span>
                    {scan.error && <div className="text-xs text-red-600 mt-1 max-w-xs truncate">{scan.error}</div>}
                  </td>
                  <td className="py-3 pr-4 text-muted-foreground">
                    <div>{scan.result_summary?.total ?? 0} registered / resolved</div>
                    <div className="text-xs">{scan.all_summary?.totalGenerated ?? '-'} permutations checked</div>
                    {(scan.result_summary?.withMx || scan.result_summary?.withNs) ? (
                      <div className="text-xs">MX: {scan.result_summary?.withMx ?? 0} · NS: {scan.result_summary?.withNs ?? 0}</div>
                    ) : null}
                    {scan.ai_analyzed_at ? (
                      <div className="text-xs">AI analyzed: {fmtTs(scan.ai_analyzed_at)}</div>
                    ) : null}
                  </td>
                  <td className="py-3 pr-4 text-muted-foreground">{scan.requested_by_email ?? '-'}</td>
                  <td className="py-3 pr-4 text-muted-foreground">
                    {scan.updated_at ? fmtTs(scan.updated_at) : '-'}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => refreshScan(scan)} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canViewResults}>Refresh</button>
                      <button onClick={() => loadResults(scan, 'registered')} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canViewResults}>View Registered</button>
                      <button onClick={() => loadResults(scan, 'all')} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canViewResults}>View All</button>
                      <button onClick={() => runAiAnalysis(scan)} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canRunAiAnalysis || scan.status !== 'completed' || aiAnalyzingId === scan.id}>{aiAnalyzingId === scan.id ? 'Analyzing…' : 'AI Analyze'}</button>
                      <button onClick={() => downloadResults(scan, 'csv', 'registered')} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canDownloadResults}>CSV</button>
                      <button onClick={() => downloadResults(scan, 'json', 'registered')} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canDownloadResults}>JSON</button>
                      <button onClick={() => downloadResults(scan, 'csv', 'all')} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canDownloadResults}>All CSV</button>
                      <button onClick={() => downloadResults(scan, 'json', 'all')} className="px-3 py-1 rounded border text-xs disabled:opacity-50" disabled={!canDownloadResults}>All JSON</button>
                    </div>
                  </td>
                </tr>
              ))}
              {scans.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    No lookalike scans have been recorded for this customer yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedResult && (
        <div className="bg-white dark:bg-card rounded-xl shadow p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-sm font-bold text-foreground">Results for {selectedResult.target}</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Viewing {selectedResult.scope === 'all' ? 'all generated permutations' : 'registered/DNS-resolving permutations'} for this scan.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Checked {selectedResult.allSummary?.totalGenerated ?? selectedResult.results?.length ?? 0} permutations; found {selectedResult.summary?.total ?? selectedResult.registeredResults?.length ?? 0} registered or DNS-resolving permutations.
              </p>
            </div>

            {selectedResult.summary?.byFuzzer && (
              <div className="text-xs text-muted-foreground max-w-2xl">
                <div><span className="font-semibold text-foreground">Top registered fuzzers: </span>{topFuzzersText(selectedResult.summary.byFuzzer)}</div>
                <div><span className="font-semibold text-foreground">Top generated fuzzers: </span>{topFuzzersText(selectedResult.allSummary?.allByFuzzer)}</div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              onClick={() => selectedResult && loadResults({ ...selectedResult, customer_id: customerId, result_summary: selectedResult.summary, all_summary: selectedResult.allSummary, requested_by_email: '', created_at: '', updated_at: '' }, 'registered')}
              className={`px-3 py-1 rounded border text-xs ${selectedResult.scope !== 'all' ? 'bg-muted' : ''}`}
              disabled={!canViewResults}
            >
              Registered / Resolved
            </button>
            <button
              onClick={() => selectedResult && loadResults({ ...selectedResult, customer_id: customerId, result_summary: selectedResult.summary, all_summary: selectedResult.allSummary, requested_by_email: '', created_at: '', updated_at: '' }, 'all')}
              className={`px-3 py-1 rounded border text-xs ${selectedResult.scope === 'all' ? 'bg-muted' : ''}`}
              disabled={!canViewResults}
            >
              All Permutations
            </button>
            <span className="text-xs text-muted-foreground ml-2">
              Result rows shown: {selectedResult.results?.length ?? 0}
            </span>
          </div>

          {selectedResult.aiAnalysis && (
            <div className="mb-5 rounded-xl border bg-muted/30 p-4 space-y-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <h4 className="text-sm font-bold text-foreground">AI Confidence Analysis</h4>
                  <p className="text-sm text-muted-foreground mt-1">{selectedResult.aiAnalysis.executiveSummary}</p>
                  <p className="text-xs text-muted-foreground mt-2">Model: {selectedResult.aiAnalysis.model ?? '-'} · Generated: {selectedResult.aiAnalysis.generatedAt ? fmtTs(selectedResult.aiAnalysis.generatedAt) : '-'}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${riskClass(selectedResult.aiAnalysis.riskLevel)}`}>{selectedResult.aiAnalysis.riskLevel.toUpperCase()}</span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">Confidence {selectedResult.aiAnalysis.overallConfidence}%</span>
                </div>
              </div>

              {selectedResult.aiAnalysis.keyFindings?.length ? (
                <div>
                  <h5 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Prioritized Findings</h5>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    {selectedResult.aiAnalysis.keyFindings.slice(0, 8).map((finding, index) => (
                      <div key={`${finding.domain}-${index}`} className="rounded-lg border bg-background p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-sm text-foreground break-all">{finding.domain}</div>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${riskClass(finding.riskLevel)}`}>{finding.riskLevel}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Fuzzer: {finding.fuzzer ?? '-'} · Confidence: {finding.confidence}%</div>
                        <ul className="list-disc pl-5 text-xs text-muted-foreground mt-2 space-y-1">
                          {(finding.reasons ?? []).slice(0, 4).map((reason, idx) => <li key={idx}>{reason}</li>)}
                        </ul>
                        <div className="text-xs mt-2"><span className="font-semibold">Action:</span> {finding.recommendedAction}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedResult.aiAnalysis.missedPermutationCandidates?.length ? (
                <div>
                  <h5 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">AI-Suggested Candidates Not Seen in Current Scanner Output</h5>
                  <div className="overflow-x-auto rounded-lg border bg-background">
                    <table className="min-w-full text-xs">
                      <thead className="bg-muted/80 text-left uppercase tracking-wide text-muted-foreground">
                        <tr><th className="py-2 px-3">Domain</th><th className="py-2 px-3">Technique</th><th className="py-2 px-3">Rationale</th></tr>
                      </thead>
                      <tbody>
                        {selectedResult.aiAnalysis.missedPermutationCandidates.slice(0, 15).map((candidate, index) => (
                          <tr key={`${candidate.domain}-${index}`} className="border-t align-top">
                            <td className="py-2 px-3 font-medium text-foreground break-all">{candidate.domain}</td>
                            <td className="py-2 px-3 text-muted-foreground">{candidate.technique}</td>
                            <td className="py-2 px-3 text-muted-foreground">{candidate.rationale}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">These are advisory candidates. They are not claimed as registered until a future DNS validation run confirms DNS or WHOIS evidence.</p>
                </div>
              ) : null}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg border bg-background p-3">
                  <h5 className="font-bold text-foreground mb-2">Next Steps</h5>
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    {(selectedResult.aiAnalysis.nextSteps ?? []).slice(0, 6).map((item, idx) => <li key={idx}>{item}</li>)}
                  </ul>
                </div>
                <div className="rounded-lg border bg-background p-3">
                  <h5 className="font-bold text-foreground mb-2">Defensive Registration Candidates</h5>
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    {(selectedResult.aiAnalysis.defensiveRegistrations ?? []).slice(0, 8).map((item, idx) => <li key={idx}>{item}</li>)}
                  </ul>
                </div>
                <div className="rounded-lg border bg-background p-3">
                  <h5 className="font-bold text-foreground mb-2">Fuzzer Recommendations</h5>
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    {(selectedResult.aiAnalysis.additionalFuzzerRecommendations ?? []).slice(0, 8).map((item, idx) => <li key={idx}>{item}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto max-h-[620px] overflow-y-auto border rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr className="text-left uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-3">Domain</th>
                  <th className="py-2 px-3">Fuzzer</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">IP Address</th>
                  <th className="py-2 px-3">AAAA</th>
                  <th className="py-2 px-3">Mail Server</th>
                  <th className="py-2 px-3">Name Server</th>
                  <th className="py-2 px-3">Created</th>
                  <th className="py-2 px-3">Registrar</th>
                </tr>
              </thead>
              <tbody>
                {(selectedResult.results ?? []).slice(0, 1000).map((row, index) => (
                  <tr key={`${row.domain}-${index}`} className="border-t align-top">
                    <td className="py-2 px-3 font-medium text-foreground">{row.domain ?? '-'}</td>
                    <td className="py-2 px-3 text-muted-foreground">{row.fuzzer ?? '-'}</td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {row.is_registered ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300">registered</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">checked</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{displayList(row.dns_a)}</td>
                    <td className="py-2 px-3 text-muted-foreground">{displayList(row.dns_aaaa)}</td>
                    <td className="py-2 px-3 text-muted-foreground">{displayList(row.dns_mx)}</td>
                    <td className="py-2 px-3 text-muted-foreground">{displayList(row.dns_ns)}</td>
                    <td className="py-2 px-3 text-muted-foreground">{row.whois_created ?? '-'}</td>
                    <td className="py-2 px-3 text-muted-foreground">{row.whois_registrar ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(selectedResult.results?.length ?? 0) > 1000 && (
            <p className="text-xs text-muted-foreground mt-3">
              Showing first 1000 rows in the portal. Download CSV/JSON for the complete result set.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
